import { Link, MonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect } from 'react';

import TopBadge from '@/components/Badges/TopBadge';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchSellersIfNeeded, getSeller } from '@/redux/modules/seller';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { getLongAddressFormat } from '@/utils/address';
import { getAuctioneerLogoUrl, getSellerUrl, imagePlaceholder } from '@/utils/urls';

const IMAGE_SIZE = 96;

type Props = {
    sellerId: number;
};

export const AuctioneerDetails = ({ sellerId }: Props) => {
    const dispatch = useAppDispatch();

    const { city, country, logoId, name, stateCode } = useAppSelector((state) => getSeller(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const location = getLongAddressFormat(city, country, stateCode);

    useEffect(() => {
        // just in case we dont yet have the seller (Auction House) object, get it
        if (!logoId) {
            dispatch(fetchSellersIfNeeded([sellerId]));
        }
    }, [dispatch, logoId, sellerId]);

    return (
        <div className={cn('flex gap-xs')}>
            <div className={cn('h-[64px] w-[64px] shrink-0 overflow-hidden rounded-md border border-border-secondary')}>
                <Link
                    alt={name}
                    className="no-underline"
                    href={getSellerUrl(sellerId, getSellerUrl(sellerId, name))}
                    target="_blank"
                >
                    <img
                        alt={name}
                        className="h-full rounded-md object-contain"
                        height={IMAGE_SIZE}
                        loading="lazy"
                        src={logoId ? getAuctioneerLogoUrl(logoId, IMAGE_SIZE, true) : imagePlaceholder}
                    />
                </Link>
            </div>
            <div className="flex flex-col gap-2xs">
                <MonochromeLink
                    alt={getSellerUrl(sellerId, name)}
                    className="no-underline"
                    href={getSellerUrl(sellerId, name)}
                    target="_blank"
                >
                    <span className="hui-text-h5">{name}</span>
                </MonochromeLink>
                <BodySecondary color="secondary">{location}</BodySecondary>

                {sellerIsTopRated && (
                    <TopBadge
                        className="mt-0 whitespace-nowrap"
                        iconSize="small"
                    />
                )}
            </div>
        </div>
    );
};
